import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import {
    getChatMessageList,
    getConversationList,
    getUnreadSummary,
    markAllMessageRead,
    markConversationRead
} from '@/api/message';
import { imWsClient } from '@/utils/im/wsClient';
import { parseNotifyExtra } from '@/utils/im/wfNotify';

const EMPTY_SUMMARY: Message.ISummary = { totalUnread: 0, systemUnread: 0, unreadConversationCount: 0 };

/**
 * 消息中心 Store（HeaderBar 角标 + 通知中心页 + IM 实时推送）
 *
 * 关键约定：
 * 1. 未读是**服务端口径**：本地只缓存接口返回值，收到 WS 帧后重新拉取汇总（不做本地加减）；
 * 2. 长连接统一走 {@link imWsClient}（xqyy_msg，code = 4 投递帧），不新增第二条通道；
 * 3. 标记已读走独立接口（/chsMessage/markRead.json），不复用 updateStatus。
 */
export const useMessageStore = defineStore('message', () => {
    // ======================== State ========================
    const summary = ref<Message.ISummary>({ ...EMPTY_SUMMARY });
    const conversations = ref<Message.IConversation[]>([]);
    const conversationTotal = ref(0);
    const conversationLoading = ref(false);

    const messages = ref<Message.IChatMessage[]>([]);
    const messageTotal = ref(0);
    const messageLoading = ref(false);

    const currentConversationId = ref('');
    const wsConnected = ref(false);

    /**
     * 系统通知结构化扩展（extraData）缓存：**只有 WS 实时帧携带 extraData**，
     * 落库的 chs_message 不存（历史消息无从解析，跳转保持原状进消息中心）。
     * - notifyExtras：会话 id → 最近一条帧的 extraData（HeaderBar 面板点击判定用）
     * - messageExtras：chs_message.id → 对应帧的 extraData（通知中心消息流点击判定用）
     */
    const notifyExtras = ref<Record<string, Record<string, any>>>({});
    const messageExtras = ref<Record<string, Record<string, any>>>({});

    // ======================== Getters ========================
    const totalUnread = computed(() => summary.value.totalUnread || 0);

    // ======================== 订阅句柄 ========================
    let frameOff: (() => void) | null = null;
    let stateOff: (() => void) | null = null;
    let refreshTimer: number | null = null;

    // ======================== Actions ========================

    /**
     * 拉取未读汇总（角标唯一数据源）
     */
    async function refreshSummary() {
        try {
            const { result } = await getUnreadSummary();
            summary.value = result ? { ...EMPTY_SUMMARY, ...result } : { ...EMPTY_SUMMARY };
        } catch (e) {
            // 拉取失败保持上一次值（不本地推断，避免角标与后端不一致）
        }
    }

    /**
     * 会话列表分页
     */
    async function loadConversations(pageNo = 1, pageSize = 20) {
        conversationLoading.value = true;
        try {
            const { result } = await getConversationList({ pageNo, pageSize });
            conversations.value = result?.result || [];
            conversationTotal.value = result?.totalCount || 0;
        } catch (e) {
            conversations.value = [];
            conversationTotal.value = 0;
        } finally {
            conversationLoading.value = false;
        }
    }

    /**
     * 会话内消息分页
     */
    async function loadMessages(conversationId: string, pageNo = 1, pageSize = 20) {
        if (!conversationId) {
            messages.value = [];
            messageTotal.value = 0;
            return;
        }
        messageLoading.value = true;
        try {
            const { result } = await getChatMessageList({ conversationId, pageNo, pageSize });
            messages.value = result?.result || [];
            messageTotal.value = result?.totalCount || 0;
        } catch (e) {
            messages.value = [];
            messageTotal.value = 0;
        } finally {
            messageLoading.value = false;
        }
    }

    /**
     * 打开会话：加载消息并标记已读（已读后刷新汇总，角标由服务端返回）
     */
    async function openConversation(conversationId: string) {
        currentConversationId.value = conversationId;
        await loadMessages(conversationId);
        await markRead(conversationId);
    }

    /**
     * 标记某会话已读
     */
    async function markRead(conversationId: string) {
        if (!conversationId) {
            return;
        }
        try {
            await markConversationRead({ conversationId });
            const target = conversations.value.find((item) => item.conversationId === conversationId);
            if (target) {
                target.unreadNum = 0;
            }
            await refreshSummary();
        } catch (e) {
            // 失败保持原状，由下一次刷新纠正
        }
    }

    /**
     * 全部已读
     */
    async function markAllRead() {
        try {
            await markAllMessageRead();
            conversations.value.forEach((item) => (item.unreadNum = 0));
            await refreshSummary();
        } catch (e) {
            // 失败保持原状
        }
    }

    /**
     * 收到 WS 推送帧：服务端汇总为准，仅触发重新拉取（300ms 合并，避免密集通知抖动）
     */
    function handleFrame(frame: Message.IWsFrame) {
        const conversationId = frame.conversationId || extractConversationId(frame);
        // 缓存帧里的结构化 extraData（仅实时帧有），供面板 / 消息中心做审批直达跳转
        const extra = parseNotifyExtra((frame as any)?.extraData);
        if (extra) {
            const convId = conversationId || String(extra.conversationId || '');
            if (convId) notifyExtras.value[convId] = extra;
            if (extra.bizMessageId !== undefined && extra.bizMessageId !== null) {
                messageExtras.value[String(extra.bizMessageId)] = extra;
            }
        }
        if (refreshTimer !== null) {
            return;
        }
        refreshTimer = window.setTimeout(async () => {
            refreshTimer = null;
            await refreshSummary();
            if (conversationId && currentConversationId.value && conversationId === currentConversationId.value) {
                await loadMessages(currentConversationId.value);
            }
            // 会话摘要（最后消息 / 时间）需要同步刷新；列表已被拉起过时才刷新，避免全局请求放大
            if (conversations.value.length > 0) {
                await loadConversations(1, 20);
            }
        }, 300);
    }

    /**
     * 启动：首次拉取 + 建立长连接（幂等，Layout 挂载时调用）
     */
    async function start() {
        await refreshSummary();
        await loadConversations(1, 20);

        if (!frameOff) {
            frameOff = imWsClient.onFrame(handleFrame);
        }
        if (!stateOff) {
            stateOff = imWsClient.onStateChange((connected) => {
                wsConnected.value = connected;
                if (connected) {
                    // 重连成功后对齐服务端未读（离线期间的推送可能已过期，以服务端为准）
                    refreshSummary();
                }
            });
        }
        imWsClient.start();
    }

    /**
     * 停止：解除订阅并断开长连接（退出登录时调用）
     */
    function stop() {
        if (frameOff) {
            frameOff();
            frameOff = null;
        }
        if (stateOff) {
            stateOff();
            stateOff = null;
        }
        if (refreshTimer !== null) {
            window.clearTimeout(refreshTimer);
            refreshTimer = null;
        }
        imWsClient.stop();
    }

    /**
     * 清空本地缓存（退出登录 / 切换商家时调用）
     */
    function reset() {
        summary.value = { ...EMPTY_SUMMARY };
        conversations.value = [];
        conversationTotal.value = 0;
        messages.value = [];
        messageTotal.value = 0;
        currentConversationId.value = '';
        wsConnected.value = false;
        notifyExtras.value = {};
        messageExtras.value = {};
    }

    return {
        // state
        summary,
        conversations,
        conversationTotal,
        conversationLoading,
        messages,
        messageTotal,
        messageLoading,
        currentConversationId,
        wsConnected,
        notifyExtras,
        messageExtras,
        // getters
        totalUnread,
        // actions
        refreshSummary,
        loadConversations,
        loadMessages,
        openConversation,
        markRead,
        markAllRead,
        handleFrame,
        start,
        stop,
        reset
    };
});

/**
 * 从 extraData 解析会话 id（兼容后端 extraData 为对象 / JSON 字符串两种形态）
 */
function extractConversationId(frame: Message.IWsFrame): string {
    const extra = frame.extraData;
    if (!extra) {
        return '';
    }
    if (typeof extra === 'string') {
        try {
            const parsed = JSON.parse(extra);
            return parsed?.conversationId || '';
        } catch (e) {
            return '';
        }
    }
    return extra.conversationId || '';
}
